import { Button, Fieldset, Heading, Radio } from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { AvtaleDto, GetAvtalePrivatResponse } from "@/api/generated";
import { DescriptionList, DescriptionTerm, FormWrapper } from "@/components";
import { BestillingFormErrorSummary } from "@/features/bestillinger";
import {
  bestillerPrivatFormSchema,
  type bestillerPrivatFormValues,
  Tekstfelt,
  translationPrefixPrivatForm,
} from "@/features/bestillinger/forms";
import { Constants } from "@/features/shared/constants";
import { formatDateStringInterval, formatPostnummer } from "@/utils";
import { EndreAvtaleFeilet } from "./EndreAvtaleFeilet";
import { usePutPrivatAvtale } from "./hooks/usePutPrivatAvtale";

function initFormValues(avtale: AvtaleDto): bestillerPrivatFormValues {
  if (avtale.avtaleType !== "Privat" || avtale.avtaleDetaljerPrivat == null) {
    throw new Error(`prop ${avtale} må være av type Privat`);
  }

  return {
    boligType: avtale.avtaleDetaljerPrivat.boligType,
    kundenummer: avtale.kundenummer ?? "",
    målerId: avtale.målerId ?? "",
    adresse: avtale.avtaleDetaljerPrivat.adresse,
    postnummer: formatPostnummer(avtale.avtaleDetaljerPrivat.postnummer),
    poststed: avtale.avtaleDetaljerPrivat.sted,
  };
}

type EndrePrivatAvtaleFormProps = {
  avtale: GetAvtalePrivatResponse;
};

export const EndrePrivatAvtaleForm = ({
  avtale: { avtale },
}: EndrePrivatAvtaleFormProps) => {
  if (avtale.avtaleType !== "Privat" || avtale.avtaleDetaljerPrivat == null) {
    throw new Error(`prop ${avtale} må være av type Privat`);
  }
  const { t } = useTranslation();
  const router = useRouter();

  const form = useForm({
    resolver: zodResolver(bestillerPrivatFormSchema),
    values: initFormValues(avtale),
    mode: "onSubmit",
  });

  const { mutate, isPending, isError } = usePutPrivatAvtale();

  const handleSubmit = (data: bestillerPrivatFormValues) => {
    mutate({
      body: {
        boligType: data.boligType,
        adresse: data.adresse,
        postNummer: Number(data.postnummer),
        sted: data.poststed,
        målerId: data.målerId,
        kundenummer: data.kundenummer,
      },
      path: {
        id: avtale.avtaleId,
      },
    });
  };

  const handleAvbryt = () => {
    router.navigate({ to: "/" });
  };

  return (
    <section>
      <FormProvider {...form}>
        <FormWrapper onSubmit={form.handleSubmit(handleSubmit)}>
          <section className="space-y-4">
            <div>
              <Heading data-size={"xs"} className="mb-3">
                {t("pages.endre.form.leverandør.tittel")}
              </Heading>
              <DescriptionList>
                <DescriptionTerm
                  tranlsationFeltKey="pages.endre.form.leverandør.navn"
                  value={avtale.leverandør.name}
                />
              </DescriptionList>
            </div>
            <div>
              <Heading data-size={"xs"} className="mb-3">
                {t("pages.endre.form.aktør.tittel")}
              </Heading>
              <DescriptionList>
                <DescriptionTerm
                  tranlsationFeltKey="pages.endre.form.aktør.navn"
                  value={avtale.bestiller.navn}
                />
              </DescriptionList>
            </div>
            <div>
              <Heading data-size={"xs"} className="mb-3">
                {t("pages.endre.form.avtaleperiode.tittel")}
              </Heading>
              <DescriptionList>
                <DescriptionTerm
                  tranlsationFeltKey="pages.endre.form.avtaleperiode.dato"
                  value={formatDateStringInterval(
                    avtale.activeFromInclusive,
                    avtale.activeToExclusive,
                  )}
                />
              </DescriptionList>
            </div>
          </section>
          <Fieldset>
            <Fieldset.Legend>
              {t(`${translationPrefixPrivatForm}.felt`, {
                context: "boligType",
              })}
            </Fieldset.Legend>
            <div className="flex flex-wrap gap-4">
              {Constants.PRIVAT_BOLIGTYPER.map((boligType) => (
                <Radio
                  key={boligType}
                  label={t("enums.boligType", { context: boligType })}
                  value={boligType}
                  {...form.register("boligType")}
                  name="boligType"
                />
              ))}
            </div>
          </Fieldset>
          <Fieldset>
            <Tekstfelt
              feltKey="kundenummer"
              maxLength={256}
              hjelpeTekst={true}
              labelTranslationPrefix={translationPrefixPrivatForm}
            />
          </Fieldset>
          <Fieldset>
            <Tekstfelt
              feltKey="målerId"
              maxLength={256}
              hjelpeTekst={true}
              labelTranslationPrefix={translationPrefixPrivatForm}
            />
          </Fieldset>
          <Fieldset>
            <Tekstfelt
              feltKey="adresse"
              maxLength={256}
              labelTranslationPrefix={translationPrefixPrivatForm}
              autoComplete="street-address"
            />
          </Fieldset>
          <Fieldset>
            <div className="flex gap-4 flex-row flex-wrap sm:flex-nowrap">
              <Tekstfelt
                feltKey="postnummer"
                type="text"
                size={4}
                minLength={4}
                maxLength={4}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="postal-code" // ZIP code
                labelTranslationPrefix={translationPrefixPrivatForm}
              />
              <Tekstfelt
                className="w-full"
                feltKey="poststed"
                maxLength={256}
                autoComplete="address-level2" // City
                labelTranslationPrefix={translationPrefixPrivatForm}
              />
            </div>
          </Fieldset>
          {isError && <EndreAvtaleFeilet />}
          <BestillingFormErrorSummary errors={form.formState.errors} />
          <div className="flex gap-4">
            <Button onClick={handleAvbryt} variant="secondary">
              <span>{t("pages.endre.knapper.avbryt")}</span>
            </Button>
            <Button type="submit" loading={isPending}>
              <span>{t("pages.endre.knapper.lagre")}</span>
            </Button>
          </div>
        </FormWrapper>
      </FormProvider>
    </section>
  );
};
